import React from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import ForceGraph2D from 'react-force-graph-2d';
import { toast } from 'sonner';
import {
  AlertTriangle, AlertCircle, Network, Link2, Activity,
  Users, X, ArrowUpRight, GitCompare,
} from 'lucide-react';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);
};

export const InvestigacionRedGlobalTab = ({
  resultados, highlightedEntity, setHighlightedEntity,
}) => (
  <div className="space-y-4">
    {/* Header con métricas */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card className="p-4 bg-gradient-to-br from-purple-50 to-white border-purple-200">
        <div className="flex items-center gap-2 mb-1">
          <Network size={16} className="text-purple-600" /><span className="text-xs text-slate-500">Nodos</span>
        </div>
        <p className="text-2xl font-bold text-purple-700">{resultados.analisis_red.metricas_red?.total_nodos || 0}</p>
      </Card>
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-200">
        <div className="flex items-center gap-2 mb-1">
          <Link2 size={16} className="text-blue-600" /><span className="text-xs text-slate-500">Conexiones</span>
        </div>
        <p className="text-2xl font-bold text-blue-700">{resultados.analisis_red.metricas_red?.total_conexiones || 0}</p>
      </Card>
      <Card className="p-4 bg-gradient-to-br from-amber-50 to-white border-amber-200">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={16} className="text-amber-600" /><span className="text-xs text-slate-500">Patrones</span>
        </div>
        <p className="text-2xl font-bold text-amber-700">{resultados.analisis_red.resumen?.patrones_detectados || 0}</p>
      </Card>
      <Card className={`p-4 border ${
        resultados.analisis_red.resumen?.riesgo_red >= 70 ? 'bg-gradient-to-br from-red-50 to-white border-red-300' :
        resultados.analisis_red.resumen?.riesgo_red >= 40 ? 'bg-gradient-to-br from-amber-50 to-white border-amber-300' :
        'bg-gradient-to-br from-green-50 to-white border-green-300'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <Activity size={16} className={
            resultados.analisis_red.resumen?.riesgo_red >= 70 ? 'text-red-600' :
            resultados.analisis_red.resumen?.riesgo_red >= 40 ? 'text-amber-600' : 'text-green-600'
          } />
          <span className="text-xs text-slate-500">Riesgo Red</span>
        </div>
        <p className={`text-2xl font-bold ${
          resultados.analisis_red.resumen?.riesgo_red >= 70 ? 'text-red-700' :
          resultados.analisis_red.resumen?.riesgo_red >= 40 ? 'text-amber-700' : 'text-green-700'
        }`}>{resultados.analisis_red.resumen?.riesgo_red || 0}%</p>
      </Card>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Grafo de Red */}
      <Card className="lg:col-span-2 p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <GitCompare size={16} className="text-purple-600" />Visualización de Red
            {highlightedEntity && (
              <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs flex items-center gap-1">
                <span>Resaltado: {highlightedEntity.slice(0, 20)}</span>
                <button onClick={() => setHighlightedEntity(null)} className="ml-1 hover:text-amber-900">
                  <X size={12} />
                </button>
              </Badge>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-100 text-purple-700 text-xs">Investigado</Badge>
            <Badge className="bg-amber-100 text-amber-700 text-xs">Hub</Badge>
            <Badge className="bg-red-100 text-red-700 text-xs">Dispersor</Badge>
            <Badge className="bg-green-100 text-green-700 text-xs">Concentrador</Badge>
            <Badge className="bg-violet-100 text-violet-700 text-xs">Intermediario</Badge>
          </div>
        </div>
        <div className="h-[400px] border rounded-lg bg-slate-50 relative">
          {resultados.analisis_red.grafo_completo?.nodes?.length > 0 ? (
            <ForceGraph2D
              graphData={{
                nodes: resultados.analisis_red.grafo_completo.nodes.map(n => ({
                  ...n, name: n.nombre || n.label, val: n.size || 10,
                  isHighlighted: highlightedEntity && (n.id === highlightedEntity || n.label === highlightedEntity)
                })),
                links: resultados.analisis_red.grafo_completo.edges.map(e => ({
                  source: e.source, target: e.target, color: e.color || '#94a3b8',
                  type: e.type, volumen: e.volumen, operaciones: e.operaciones,
                  isHighlighted: highlightedEntity && (e.source === highlightedEntity || e.target === highlightedEntity)
                }))
              }}
              nodeColor={node => node.isHighlighted ? '#f59e0b' : node.color || '#64748b'}
              nodeLabel={node => {
                const nombre = node.nombre || '';
                const id = node.id || node.label;
                const tipo = node.type === 'investigado' ? 'Cliente Investigado' :
                  node.type === 'hub' ? 'Hub (Alta conectividad)' :
                  node.type === 'dispersor' ? 'Dispersor' :
                  node.type === 'concentrador' ? 'Concentrador' : 'Contraparte';
                return nombre ? `${nombre}\n${id}\nTipo: ${tipo}` : `${id}\nTipo: ${tipo}`;
              }}
              linkLabel={link => {
                const tipo = link.type === 'ingreso' ? 'Ingreso' : 'Egreso';
                const vol = link.volumen ? `$${link.volumen.toLocaleString()}` : '';
                const ops = link.operaciones ? `${link.operaciones} ops` : '';
                return `${tipo}${vol ? ` - ${vol}` : ''}${ops ? ` (${ops})` : ''}`;
              }}
              linkColor={link => link.isHighlighted ? '#f59e0b' : link.color}
              linkWidth={link => {
                const baseWidth = Math.max(1.5, Math.min(4, Math.log10((link.volumen || 1000) / 1000) + 1));
                return link.isHighlighted ? baseWidth + 2 : baseWidth;
              }}
              nodeRelSize={6}
              linkDirectionalArrowLength={4}
              linkDirectionalArrowRelPos={1}
              backgroundColor="#f8fafc"
              width={700}
              height={380}
              onNodeClick={(node) => {
                const nodeId = node.id || node.label;
                setHighlightedEntity(highlightedEntity === nodeId ? null : nodeId);
                toast.info(`${node.nombre || node.id}\nTipo: ${node.type}`);
              }}
              nodeCanvasObject={(node, ctx, globalScale) => {
                const isHighlighted = node.isHighlighted;
                const size = isHighlighted ? 14 : node.type === 'investigado' ? 12 : node.type === 'hub' ? 10 : 8;
                if (isHighlighted) {
                  ctx.beginPath();
                  ctx.arc(node.x, node.y, size + 4, 0, 2 * Math.PI);
                  ctx.fillStyle = 'rgba(245, 158, 11, 0.3)';
                  ctx.fill();
                }
                ctx.beginPath();
                ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
                ctx.fillStyle = isHighlighted ? '#f59e0b' : (node.color || '#64748b');
                ctx.fill();
                ctx.strokeStyle = isHighlighted ? '#d97706' : node.type === 'investigado' ? '#7c3aed' : '#475569';
                ctx.lineWidth = isHighlighted ? 3 : node.type === 'investigado' ? 2 : 1;
                ctx.stroke();
                const label = node.nombre || node.label || '';
                if (label && (globalScale > 0.5 || isHighlighted)) {
                  ctx.font = `${isHighlighted ? 'bold ' : ''}${Math.max(6, 9/globalScale)}px Sans-Serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillStyle = isHighlighted ? '#92400e' : '#1e293b';
                  ctx.fillText(label.slice(0, 15), node.x, node.y + size + 8);
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400">
              <div className="text-center">
                <Network size={40} className="mx-auto mb-2 opacity-50" />
                <p>No hay suficientes datos para visualizar la red</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Entidades Clave */}
      <Card className="p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Users size={16} className="text-amber-600" />Entidades Clave Detectadas
        </h3>
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {/* Hubs */}
          {resultados.analisis_red.entidades_clave?.hubs?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                Hubs ({resultados.analisis_red.entidades_clave.hubs.length})
              </h4>
              <div className="space-y-2">
                {resultados.analisis_red.entidades_clave.hubs.slice(0, 5).map((hub, idx) => (
                  <div key={idx}
                    className={`p-2 rounded text-xs border cursor-pointer transition-all ${
                      highlightedEntity === hub.cuenta
                        ? 'bg-amber-200 border-amber-400 ring-2 ring-amber-300'
                        : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                    }`}
                    onClick={() => {
                      setHighlightedEntity(highlightedEntity === hub.cuenta ? null : hub.cuenta);
                      toast.info(`Hub: ${hub.cuenta}\n${hub.entradas} entradas, ${hub.salidas} salidas`);
                    }}>
                    <p className="font-mono text-amber-900 truncate">{hub.cuenta}</p>
                    <div className="flex gap-2 mt-1 text-amber-700">
                      <span>&darr;{hub.entradas} entradas</span><span>&uarr;{hub.salidas} salidas</span>
                    </div>
                    {hub.es_investigado && (<Badge className="mt-1 bg-purple-100 text-purple-700 text-xs">Investigado</Badge>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dispersores */}
          {resultados.analisis_red.entidades_clave?.dispersores?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                Dispersores ({resultados.analisis_red.entidades_clave.dispersores.length})
              </h4>
              <div className="space-y-2">
                {resultados.analisis_red.entidades_clave.dispersores.slice(0, 5).map((d, idx) => (
                  <div key={idx}
                    className={`p-2 rounded text-xs border cursor-pointer transition-all ${
                      highlightedEntity === d.cuenta
                        ? 'bg-red-200 border-red-400 ring-2 ring-red-300'
                        : 'bg-red-50 border-red-200 hover:bg-red-100'
                    }`}
                    onClick={() => {
                      setHighlightedEntity(highlightedEntity === d.cuenta ? null : d.cuenta);
                      toast.info(`Dispersor: ${d.cuenta}\n${d.destinos} destinos`);
                    }}>
                    <p className="font-mono text-red-900 truncate">{d.cuenta}</p>
                    <p className="text-red-700 mt-1">&rarr; {d.destinos} destinos &middot; {formatMoney(d.volumen)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Concentradores */}
          {resultados.analisis_red.entidades_clave?.concentradores?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                Concentradores ({resultados.analisis_red.entidades_clave.concentradores.length})
              </h4>
              <div className="space-y-2">
                {resultados.analisis_red.entidades_clave.concentradores.slice(0, 5).map((c, idx) => (
                  <div key={idx}
                    className={`p-2 rounded text-xs border cursor-pointer transition-all ${
                      highlightedEntity === c.cuenta
                        ? 'bg-green-200 border-green-400 ring-2 ring-green-300'
                        : 'bg-green-50 border-green-200 hover:bg-green-100'
                    }`}
                    onClick={() => {
                      setHighlightedEntity(highlightedEntity === c.cuenta ? null : c.cuenta);
                      toast.info(`Concentrador: ${c.cuenta}\n${c.fuentes} fuentes`);
                    }}>
                    <p className="font-mono text-green-900 truncate">{c.cuenta}</p>
                    <p className="text-green-700 mt-1">&larr; {c.fuentes} fuentes &middot; {formatMoney(c.volumen)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Intermediarios */}
          {resultados.analisis_red.entidades_clave?.intermediarios?.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-violet-700 mb-2 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                Intermediarios ({resultados.analisis_red.entidades_clave.intermediarios.length})
              </h4>
              <div className="space-y-2">
                {resultados.analisis_red.entidades_clave.intermediarios.slice(0, 5).map((i, idx) => (
                  <div key={idx}
                    className={`p-2 rounded text-xs border cursor-pointer transition-all ${
                      highlightedEntity === i.cuenta
                        ? 'bg-violet-200 border-violet-400 ring-2 ring-violet-300'
                        : 'bg-violet-50 border-violet-200 hover:bg-violet-100'
                    }`}
                    onClick={() => {
                      setHighlightedEntity(highlightedEntity === i.cuenta ? null : i.cuenta);
                      toast.info(`Intermediario: ${i.cuenta}\n${i.conexiones_in} entradas, ${i.conexiones_out} salidas`);
                    }}>
                    <p className="font-mono text-violet-900 truncate">{i.cuenta}</p>
                    <p className="text-violet-700 mt-1">Conecta {i.num_conexiones} clientes investigados</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sin entidades */}
          {(!resultados.analisis_red.entidades_clave?.hubs?.length &&
            !resultados.analisis_red.entidades_clave?.dispersores?.length &&
            !resultados.analisis_red.entidades_clave?.concentradores?.length &&
            !resultados.analisis_red.entidades_clave?.intermediarios?.length) && (
            <p className="text-sm text-slate-400 text-center py-4">No se detectaron entidades clave</p>
          )}
        </div>
      </Card>
    </div>

    {/* Patrones de Red y Conexiones Sospechosas */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {resultados.analisis_red.patrones_red?.length > 0 && (
        <Card className="p-4 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-600" />Patrones de Red Detectados
          </h3>
          <div className="space-y-3">
            {resultados.analisis_red.patrones_red.map((pattern, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${
                pattern.severidad === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={pattern.severidad === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {pattern.tipo.replace(/_/g, ' ')}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {pattern.severidad === 'high' ? 'Alto Riesgo' : 'Riesgo Medio'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-700">{pattern.descripcion}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {resultados.analisis_red.conexiones_sospechosas?.length > 0 && (
        <Card className="p-4 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-600" />Conexiones Sospechosas
          </h3>
          <div className="space-y-2">
            {resultados.analisis_red.conexiones_sospechosas.map((conn, idx) => (
              <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Badge className="bg-amber-100 text-amber-700 text-xs mb-2">
                  {conn.tipo.replace(/_/g, ' ')}
                </Badge>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-slate-600 truncate max-w-[100px]">{conn.origen}</span>
                  <ArrowUpRight size={14} className="text-amber-600" />
                  <span className="font-mono text-xs text-slate-600 truncate max-w-[100px]">{conn.destino}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{conn.descripcion}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  </div>
);

export default InvestigacionRedGlobalTab;
